export interface EventData {
  id?: string;
  name: string;
  slug?: string;
  description: string;
  gender: string;
  sport_id: string;
  event_type_id: string;
  event_category_ids: string[];
  start_date: string;
  end_date: string;
  registration_open_date: string;
  registration_close_date: string;
  more_info: string;
  thumbnail_url: string;

  // Venue
  main_venue_id: string;
  additional_venue_ids: string[];

  // Registration
  accept_online_registrations: boolean;
  allow_checkin: boolean;
  allow_coupons: boolean;
  discount_policy_id: string;
  external_registration_url: string;
  registration_type: string;
  roster_deadline: string;
  waitlist_enabled: boolean;
  waitlist_message: string;
  accept_special_requests: boolean;
  special_request_instructions: string;
  default_special_request_fee: number;

  // Display / Details
  event_format: string;
  checkin_short_description?: string | null;
  checkin_instructions: string;
  rules_short_description?: string | null;
  rules: string;
  schedule_info: string;
  pricing_notes: string;
  age_groups_description: string;
  background_image_url: string;
  hotels_short_description?: string | null;
  hotel_booking_url: string;

  // Stay-to-play
  stay_to_play_enabled: boolean;
  stay_to_play_mode: 'simple_url' | 'partner_managed' | 'reservetravel' | null;
  stay_to_play_required: boolean;
  stay_to_play_notes: string;
  // Teams whose home ZIP is within this many miles of the main venue are
  // automatically exempt from hotel requirements. Null = off.
  hotel_exempt_radius_miles: number | null;
  promo_video_url: string;
  live_stream_short_description?: string | null;
  live_streaming_description: string;
  live_streaming_url: string;
  gallery_images: string[];
  tickets_short_description?: string | null;
  extras_short_description?: string | null;
  schedule_requests_short_description?: string | null;
  ticket_display_text: string;

  // Media credentials
  enable_media_credentials: boolean;
  media_credentials_short_description?: string | null;
  media_credentials_price: number | null;
  media_credentials_description: string;
  media_credentials_form_id: string;
  media_credentials_notification_email: string;

  // Coach info
  enable_coach_info: boolean;
  coaches_short_description?: string | null;
  coach_info_description: string;
  coach_info_form_id: string;
  coach_notification_email: string;
  coaches_external_url: string;

  // Coach attendance / media credentials (D15)
  coach_attendance_enabled?: boolean;
  coach_attendance_paid?: boolean;
  is_ncaa_live_event?: boolean;
  media_credentials_enabled?: boolean;
  media_credentials_paid?: boolean;

  // Notifications
  registration_alert_emails: string[];
  payment_notification_emails: string[];
  roster_upload_emails: string[];

  // Waivers
  waiver_required_at_registration: boolean;
  waiver_text: string;
  waiver_url: string;
  waiver_version: number;

  // Sponsors
  selected_sponsor_ids: string[];

  // Meta
  status: string;
  organization_id: string;
  is_active: boolean;
}

export interface Venue {
  id: string;
  name: string;
  address: string;
  city: string;
  state: string;
  zip_code: string;
  phone?: string;
  website?: string;
  organization_id?: string;
}

export interface Sport {
  id: string;
  name: string;
  slug: string;
}

export interface EventType {
  id: string;
  name: string;
}

export interface EventCategory {
  id: string;
  name: string;
}

export interface Sponsor {
  id: string;
  name: string;
  logo_url: string;
  website_url: string;
  organization_id?: string;
}

export interface EventSponsor {
  id: string;
  event_id: string;
  sponsor_id: string;
  display_order: number;
}

export interface PricingTier {
  id?: string;
  age_group: string;
  gender: "boys" | "girls";
  price: number;
  max_teams?: number | null;
  accepts_special_requests: boolean;
  special_request_notes: string;
  special_request_fee: number;
  is_active: boolean;
}

export type PricingGenderFilter = "all" | "boys" | "girls";

const STANDARD_AGE_GROUPS = [
  "6U", "7U", "8U", "9U", "10U", "11U", "12U", "13U", "14U", "15U", "16U", "17U", "18U",
  "9th Grade", "10th Grade", "11th Grade", "12th Grade", "HS Open",
] as const;

export function createDefaultPricingTiers(): PricingTier[] {
  const tiers: PricingTier[] = [];
  for (const gender of ["boys", "girls"] as const) {
    for (const ag of STANDARD_AGE_GROUPS) {
      tiers.push({
        age_group: ag,
        gender,
        price: 0,
        max_teams: null,
        accepts_special_requests: false,
        special_request_notes: "",
        special_request_fee: 0,
        is_active: true,
      });
    }
  }
  return tiers;
}

export function getDefaultGenderFilter(gender: string | null | undefined): PricingGenderFilter {
  if (!gender) return "all";
  const g = gender.toLowerCase();
  if (g === "men" || g === "boys") return "boys";
  if (g === "women" || g === "girls") return "girls";
  return "all";
}

export interface EventExtra {
  id?: string;
  event_id?: string;
  name: string;
  price: number;
  max_quantity_per_registration?: number;
  description: string;
  sort_order: number;
  is_active: boolean;
  /** 'team' extras need only a quantity; 'player' extras collect player details. */
  level?: "team" | "player";
  requires_team?: boolean;
  requires_player?: boolean;
  requires_game_date?: boolean;
}

export interface TicketOption {
  id?: string;
  event_id?: string;
  name: string;
  price: number;
  description: string;
  sort_order: number;
  is_active: boolean;
}

export interface EventFaq {
  id?: string;
  event_id?: string;
  question: string;
  answer: string;
  category: string;
  sort_order: number;
  is_active: boolean;
}

export interface FaqCatalogItem {
  id: string;
  organization_id: string | null;
  question: string;
  answer: string;
  category: string;
  sport_id: string | null;
  is_active: boolean;
  display_order: number;
  created_at: string;
  updated_at: string;
}

export interface EventFaqLink {
  id: string;
  event_id: string;
  faq_id: string;
  override_answer: string | null;
  display_order: number;
  is_active: boolean;
}

export interface WaiverSignature {
  id: string;
  event_id: string;
  registration_id?: string;
  signer_name: string;
  signer_email: string;
  signer_relationship?: string;
  athlete_name?: string;
  athlete_dob?: string;
  waiver_version: number;
  waiver_text_snapshot: string;
  signed_at: string;
  ip_address?: string;
  signature_image_url?: string;
  waiver_catalog_id?: string | null;
  waiver_type?: 'team' | 'individual' | null;
  master_team_id?: string | null;
  extra_field_data?: Record<string, any> | null;
  signature_method?: 'typed_name' | 'drawn_signature' | 'click_to_sign' | null;
}

export interface WaiverField {
  key: string;
  label: string;
  type: 'text' | 'date' | 'phone' | 'email' | 'textarea' | 'checkbox';
  required: boolean;
  helper_text?: string;
}

export interface WaiverCatalogItem {
  id: string;
  organization_id: string | null;
  title: string;
  description: string | null;
  sport_id: string | null;
  waiver_type: 'team' | 'individual' | 'both';
  source_type: 'text' | 'pdf_upload' | 'external_url';
  body_html: string | null;
  pdf_storage_path: string | null;
  pdf_extracted_text: string | null;
  pdf_filename: string | null;
  pdf_size_bytes: number | null;
  pdf_uploaded_at: string | null;
  external_url: string | null;
  signature_mode: 'typed_name' | 'drawn_signature' | 'click_to_sign';
  required_fields: WaiverField[];
  version: number;
  is_active: boolean;
  created_at?: string;
  updated_at?: string;
}

export interface EventWaiverSettings {
  id?: string;
  event_id: string;
  team_waiver_id: string | null;
  individual_waiver_id: string | null;
  team_waiver_required: boolean;
  individual_waiver_required: boolean;
  collect_at: 'registration' | 'before_event' | 'on_arrival';
  block_unsigned: boolean;
  team_waiver_version_pinned: number | null;
  individual_waiver_version_pinned: number | null;
}

export interface DiscountPolicy {
  id: string;
  name: string;
  description: string;
}

export interface CatalogDivision {
  id: string;
  organization_id: string | null;
  sport_id: string;
  label: string;
  display_order: number;
  is_active: boolean;
}

export interface ConstraintTemplate {
  id: string;
  organization_id: string | null;
  sport_id: string | null;
  constraint_type:
    | "team_blackout"
    | "must_play_before"
    | "must_play_after"
    | "coach_conflict"
    | "venue_preference"
    | "game_count_limit"
    | "divisional_ordering";
  label: string;
  description: string | null;
  default_severity: "hard" | "soft" | "preference";
  requires_review: boolean;
  default_fee: number;
  default_parameters: Record<string, any>;
  coach_input_fields: string[];
  display_order: number;
  is_active: boolean;
}

export interface EventConstraintOffering {
  id: string;
  event_id: string;
  template_id: string;
  override_fee: number | null;
  override_severity: "hard" | "soft" | "preference" | null;
  override_requires_review: boolean | null;
  is_active: boolean;
  display_order: number;
}

export interface EventDivisionSettings {
  id: string;
  event_id: string;
  catalog_division_id: string;
  has_boys: boolean;
  has_girls: boolean;
  boys_price: number;
  girls_price: number;
  boys_max_teams: number | null;
  girls_max_teams: number | null;
  accepts_special_requests: boolean;
  special_request_notes: string;
  special_request_fee: number;
  is_active: boolean;
  display_order: number;
}

export const DEFAULT_EVENT: EventData = {
  name: "",
  description: "",
  gender: "",
  sport_id: "",
  event_type_id: "",
  event_category_ids: [],
  start_date: "",
  end_date: "",
  registration_open_date: "",
  registration_close_date: "",
  more_info: "",
  thumbnail_url: "",
  main_venue_id: "",
  additional_venue_ids: [],
  accept_online_registrations: true,
  allow_checkin: false,
  allow_coupons: false,
  discount_policy_id: "",
  external_registration_url: "",
  registration_type: "Team",
  roster_deadline: "",
  waitlist_enabled: false,
  waitlist_message: "",
  accept_special_requests: false,
  special_request_instructions: "",
  default_special_request_fee: 0,
  event_format: "",
  checkin_short_description: "",
  checkin_instructions: "",
  rules_short_description: "",
  rules: "",
  schedule_info: "",
  pricing_notes: "",
  age_groups_description: "",
  background_image_url: "",
  hotels_short_description: "",
  hotel_booking_url: "",
  stay_to_play_enabled: false,
  stay_to_play_mode: null,
  stay_to_play_required: false,
  stay_to_play_notes: "",
  hotel_exempt_radius_miles: 70,
  promo_video_url: "",
  live_stream_short_description: "",
  live_streaming_description: "",
  live_streaming_url: "",
  gallery_images: [],
  tickets_short_description: "",
  extras_short_description: "",
  schedule_requests_short_description: "",
  ticket_display_text: "",
  enable_media_credentials: false,
  media_credentials_short_description: "",
  media_credentials_price: null,
  media_credentials_description: "",
  media_credentials_form_id: "",
  media_credentials_notification_email: "",
  enable_coach_info: false,
  coaches_short_description: "",
  coach_info_description: "",
  coach_info_form_id: "",
  coach_notification_email: "",
  coaches_external_url: "",
  registration_alert_emails: [],
  payment_notification_emails: [],
  roster_upload_emails: [],
  waiver_required_at_registration: false,
  waiver_text: "",
  waiver_url: "",
  waiver_version: 1,
  selected_sponsor_ids: [],
  status: "draft",
  organization_id: "",
  is_active: true,
};

// ===== Hotels / Stay-to-Play =====
export interface HotelEventAssignment {
  id: string;
  event_id: string;
  hotel_partner_user_id: string | null;
  booking_url: string | null;
  group_rate_expiry: string | null;
  notes: string | null;
  reservetravel_group_id: string | null;
  reservetravel_group_url: string | null;
  external_event_id: string | null;
  assigned_by: string | null;
}

export interface HotelProperty {
  id: string;
  event_id: string;
  hotel_assignment_id: string | null;
  hotel_name: string;
  address: string | null;
  city: string | null;
  state: string | null;
  zip: string | null;
  distance_miles: number | null;
  group_rate_per_night: number | null;
  rate_valid_until: string | null;
  booking_link: string | null;
  star_rating: number | null;
  amenities: string[];
  photo_url: string | null;
  is_primary: boolean;
  rooms_contracted: number | null;
  rebate_per_room: number | null;
  commission_pct: number | null;
  rfp_status: string | null;
  hotel_contact_name: string | null;
  hotel_contact_email: string | null;
  hotel_contact_phone: string | null;
}

export interface HotelBooking {
  id: string;
  event_id: string;
  registration_id: string | null;
  hotel_property_id: string | null;
  team_name: string | null;
  coach_email: string | null;
  hotel_name: string | null;
  check_in_date: string | null;
  check_out_date: string | null;
  number_of_rooms: number | null;
  rate_per_night: number | null;
  gross_revenue: number | null;
  commission_earned: number | null;
  rebate_earned: number | null;
  status: string | null;
  payout_status: string | null;
}

export interface HotelRfpRequest {
  id: string;
  event_id: string;
  hotel_partner_user_id: string;
  hotel_name: string;
  contact_email: string | null;
  rooms_requested: number | null;
  desired_rate: number | null;
  status: 'draft' | 'sent' | 'responded' | 'accepted' | 'declined' | 'expired';
  sent_at: string | null;
  offered_rate: number | null;
}

export type TrackingType = 'comp' | 'pay_at_door' | 'invited' | 'partner' | 'sponsor' | 'staff' | 'other';
export type TrackingStatus = 'tentative' | 'confirmed' | 'no_show' | 'cancelled' | 'converted';
export type TrackingPaymentStatus = 'not_required' | 'pending' | 'partial' | 'paid' | 'waived' | 'refunded';

export interface EventTeamTracking {
  id: string;
  event_id: string;
  master_team_id: string | null;
  team_name: string;
  catalog_division_id: string | null;
  division_label: string | null;
  gender: 'boys' | 'girls' | null;
  tracking_type: TrackingType;
  expected_amount: number;
  amount_received: number;
  payment_status: TrackingPaymentStatus;
  payment_received_at: string | null;
  payment_method: string | null;
  payment_notes: string | null;
  contact_name: string | null;
  contact_email: string | null;
  contact_phone: string | null;
  expected_roster_size: number | null;
  notes: string | null;
  internal_notes: string | null;
  include_in_schedule: boolean;
  status: TrackingStatus;
  converted_registration_id: string | null;
  converted_at: string | null;
  added_by: string | null;
  created_at: string;
  updated_at: string;
}

export type CoachDivision = 'D1' | 'D2' | 'D3' | 'NAIA' | 'JUCO' | 'HS' | 'Other';
export const COACH_DIVISIONS: CoachDivision[] = ['D1','D2','D3','NAIA','JUCO','HS','Other'];

export interface EventCoach {
  id: string;
  event_id: string;
  coach_name: string;
  coach_email: string | null;
  coach_phone: string | null;
  college_name: string | null;
  division: CoachDivision | null;
  logo_url: string | null;
  external_url: string | null;
  hide_from_public: boolean;
  display_order: number;
  payment_status: 'unpaid' | 'pending' | 'paid' | 'refunded' | 'comp' | 'waived';
  amount_paid_cents: number;
  stripe_session_id: string | null;
  filler_name: string | null;
  filler_email: string | null;
  form_data: Record<string, any>;
  is_ncaa_live: boolean;
  notes: string | null;
}

export interface EventCoachPricing {
  id: string;
  event_id: string;
  division: CoachDivision;
  base_price: number;
  hospitality_per_day_price: number;
  additional_coach_price: number;
  is_active: boolean;
  display_order: number;
}

export interface MediaCredential {
  id: string;
  event_id: string;
  name: string;
  email: string;
  phone: string | null;
  organization: string | null;
  instagram_handle: string | null;
  twitter_handle: string | null;
  coverage_type: string | null;
  form_data: Record<string, any>;
  status: 'pending' | 'approved' | 'denied' | 'issued' | 'revoked';
  reviewed_by: string | null;
  reviewed_at: string | null;
  denial_reason: string | null;
  payment_status: 'unpaid' | 'pending' | 'paid' | 'refunded' | 'comp' | 'waived';
  amount_paid_cents: number;
  stripe_session_id: string | null;
  notes: string | null;
}

export type FormFieldType =
  | 'text' | 'email' | 'phone' | 'number'
  | 'select' | 'multi_select' | 'textarea' | 'repeater' | 'checkbox';

export interface FormFieldConfig {
  key: string;
  label: string;
  type: FormFieldType;
  required: boolean;
  placeholder?: string;
  helper_text?: string;
  options?: string[];
  order: number;
}

export interface EventFormConfig {
  id: string;
  event_id: string;
  form_type: 'coach_attendance' | 'media_credentials';
  fields: FormFieldConfig[];
  form_title: string | null;
  form_description: string | null;
  thank_you_message: string | null;
  notification_emails: string[];
  is_enabled: boolean;
}

export const DEFAULT_COACH_FIELDS: FormFieldConfig[] = [
  { key: 'filler_name', label: 'Your Name (filling out form)', type: 'text', required: true, order: 1 },
  { key: 'filler_email', label: 'Your Email', type: 'email', required: true, order: 2 },
  { key: 'coach_name', label: 'Coach Name (if different from filler)', type: 'text', required: true, order: 3 },
  { key: 'college_name', label: 'College / University', type: 'text', required: true, order: 4 },
  { key: 'division', label: 'Division', type: 'select', required: true, options: ['D1','D2','D3','NAIA','JUCO','HS','Other'], order: 5 },
  { key: 'coach_phone', label: 'Phone Number', type: 'phone', required: false, order: 6 },
  { key: 'hospitality_days', label: 'Hospitality Days', type: 'multi_select', required: false, options: ['Friday','Saturday','Sunday','All Days'], order: 7 },
  { key: 'additional_coaches', label: 'Additional Coaches', type: 'repeater', required: false, order: 8 },
];

export const DEFAULT_MEDIA_FIELDS: FormFieldConfig[] = [
  { key: 'name', label: 'Your Name', type: 'text', required: true, order: 1 },
  { key: 'email', label: 'Your Email', type: 'email', required: true, order: 2 },
  { key: 'phone', label: 'Cell Phone', type: 'phone', required: true, order: 3 },
  { key: 'organization', label: 'Publication / Organization', type: 'text', required: true, order: 4 },
  { key: 'instagram_handle', label: 'Instagram Handle (or N/A)', type: 'text', required: false, order: 5 },
  { key: 'twitter_handle', label: 'X / Twitter Handle (or N/A)', type: 'text', required: false, order: 6 },
  { key: 'coverage_type', label: 'Type of Coverage', type: 'select', required: true, options: ['Photography','Video','Written','Live Broadcast','Multiple'], order: 7 },
];
